import {
  Alert,
  Box,
  Card,
  CardContent,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { DataSourceBadge } from '../../shared/data/DataSourceBadge';
import { PageState } from '../../shared/data/PageState';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { PageHeader } from '../../shared/layout/PageHeader';
import { toPageError } from '../partners/PartnerListPage';
import { transactionGapRepositoryToken, type TransactionFailureQueueItem } from './mockRepository';

export function TransactionFailureQueuePage() {
  const { t } = useTranslation();
  const repository = useRepository(transactionGapRepositoryToken);
  const [refreshKey, setRefreshKey] = useState(0);
  const resource = useResource(
    (signal) => repository.listFailures(signal),
    [repository, refreshKey],
    (items) => items.length === 0,
  );
  return (
    <Box sx={{ minWidth: 0 }}>
      <PageHeader
        actions={<DataSourceBadge source="mock" />}
        subtitle={t('transactionFailures.subtitle')}
        title={t('transactionFailures.title')}
      />
      <Alert severity="info" sx={{ mb: 2 }}>
        {t('transactionFailures.mockOnly')}
      </Alert>
      <PageState
        emptyMessage={t('transactionFailures.empty')}
        error={toPageError(resource.error, t('transactionFailures.errors.load'))}
        onRetry={() => setRefreshKey((value) => value + 1)}
        status={resource.status}
      >
        {resource.data === null ? null : <FailureResults items={resource.data} />}
      </PageState>
    </Box>
  );
}

function FailureResults({ items }: { readonly items: readonly TransactionFailureQueueItem[] }) {
  const { i18n, t } = useTranslation();
  const language: TimeLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  if (mobile)
    return (
      <Stack aria-label={t('transactionFailures.tableLabel')} component="ul" spacing={1}>
        {items.map((item) => (
          <Card component="li" key={item.id} variant="outlined">
            <CardContent>
              <Typography component="h2" variant="subtitle1">
                {t('transactionFailures.fields.errorCode')}: {item.errorCode}
              </Typography>
              <Typography variant="body2">
                {t('transactionFailures.fields.fieldErrors')}: {item.fieldErrors.join(', ')}
              </Typography>
              <Typography variant="body2">
                {t('transactionFailures.fields.partner')}: {item.partner}
              </Typography>
              <Typography variant="body2">
                {t('transactionFailures.fields.firstOccurrence')}:{' '}
                {displayTime(item.firstOccurredAt, language)}
              </Typography>
              <Typography variant="body2">
                {t('transactionFailures.fields.lastOccurrence')}:{' '}
                {displayTime(item.lastOccurredAt, language)}
              </Typography>
              <Typography variant="body2">
                {t('transactionFailures.fields.retryCount')}: {item.retryCount}
              </Typography>
              <Typography variant="body2">
                {t('transactionFailures.fields.supportNotes')}: {item.supportNotes}
              </Typography>
              <Typography variant="body2">
                {t('transactionFailures.fields.requestId')}: {item.requestId}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Stack>
    );
  const columns = [
    'errorCode',
    'fieldErrors',
    'partner',
    'firstOccurrence',
    'lastOccurrence',
    'retryCount',
    'supportNotes',
    'requestId',
  ] as const;
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table aria-label={t('transactionFailures.tableLabel')} size="small">
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell key={column}>{t(`transactionFailures.fields.${column}`)}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => (
            <TableRow key={item.id}>
              <TableCell>{item.errorCode}</TableCell>
              <TableCell>{item.fieldErrors.join(', ')}</TableCell>
              <TableCell>{item.partner}</TableCell>
              <TableCell>{displayTime(item.firstOccurredAt, language)}</TableCell>
              <TableCell>{displayTime(item.lastOccurredAt, language)}</TableCell>
              <TableCell>{item.retryCount}</TableCell>
              <TableCell>{item.supportNotes}</TableCell>
              <TableCell>{item.requestId}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function displayTime(timestamp: string, language: TimeLanguage): string {
  return formatTimestamp(timestamp, 'Asia/Bangkok', language).display;
}
